import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { HiOutlineUser, HiOutlinePencil, HiOutlineBell, HiOutlineLink, HiOutlineTrash, HiOutlinePlus, HiOutlineBadgeCheck } from 'react-icons/hi';
import api from '../config/axios';
import { API_ENDPOINTS } from '../config/api';
import dayjs from 'dayjs';
import { useButtonStyles } from '../utils/buttonStyles';
import { useAlert } from '../contexts/AlertContext';
import SupervisorSelection from './common/SupervisorSelection';
import SubstituteUserSelection from './common/SubstituteUserSelection';
import PasswordInput from './common/PasswordInput';
import DefaultAvatar from './common/DefaultAvatar';
import AvatarCropModal from './common/AvatarCropModal';
import AvatarPreviewModal from './common/AvatarPreviewModal';
import { useAuth } from '../contexts/AuthContext';
import { INSURANCE_TYPE, INSURANCE_TYPE_DISPLAY_NAME_MAP } from './common/InsuranceTypes';
import ServiceTermsViewModal from './ServiceTermsViewModal';

const AVATAR_ACCEPT = 'image/png,image/jpeg,.png,.jpg,.jpeg';
const AVATAR_MAX_BYTES = 10 * 1024 * 1024;
const PROFILE_MESSAGE_MAX_LENGTH = 30;

const GOOGLE_CALENDAR_AUTH_EXPIRED_PROFILE_MESSAGE = '구글 계정 인증 만료, 다시 연동해주세요.';

/** YYYY-MM-DD에 일수 더하기 (로컬 달력) */
const addDaysToYmd = (ymd, deltaDays) => {
  if (!ymd || typeof ymd !== 'string') return '';
  const parts = ymd.split('-').map((n) => parseInt(n, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return '';
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  d.setDate(d.getDate() + deltaDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** 두 날짜 달력상 일수 차이 (같은 날=0, 다음날=1) */
const ymdSpanDays = (ymdStart, ymdEnd) => {
  if (!ymdStart || !ymdEnd) return 0;
  const [ys, ms, ds] = ymdStart.split('-').map((n) => parseInt(n, 10));
  const [ye, me, de] = ymdEnd.split('-').map((n) => parseInt(n, 10));
  if ([ys, ms, ds, ye, me, de].some((n) => Number.isNaN(n))) return 0;
  const a = new Date(ys, ms - 1, ds);
  const b = new Date(ye, me - 1, de);
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
};

/** 입력란 텍스트가 넘칠 때 최신 입력(커서)이 보이도록 내부 스크롤 */
const scrollInputToCaret = (e) => {
  e.target.scrollLeft = e.target.scrollWidth;
};

const ProfileModal = ({ visible, onClose, user, onUpdate }) => {
  useButtonStyles(); // buttonStyles 미사용
  const { showAlert, showConfirm } = useAlert();
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [googleAccountsLoading, setGoogleAccountsLoading] = useState(false);
  const [addingAccount, setAddingAccount] = useState(false);
  const [_regions, setRegions] = useState([]);
  const [_companies, setCompanies] = useState([]); // IS 타입 회사 목록
  const [agentCodes, setAgentCodes] = useState([]); // 설계사코드 목록
  const [agentCodeSortOrder, setAgentCodeSortOrder] = useState('korean'); // 'korean' 또는 'alphabet'
  const [_formData, setFormData] = useState({
    name: '',
    phone: '',
    regionId: null,
    supervisorEmail: '',
    departmentId: null,
    googleCalendarSyncEnabled: false,
    googleEmail: '',
    userCode: '',
    vacationStartDate: '',
    vacationEndDate: ''
  });
  const [newAccountData, setNewAccountData] = useState({
    googleEmail: '',
    accountName: ''
  });
  const [_formErrors, _setFormErrors] = useState({});
  const [newAccountErrors, setNewAccountErrors] = useState({});
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [serviceTermsModalVisible, setServiceTermsModalVisible] = useState(false);
  const [deletingAccountId, setDeletingAccountId] = useState(null);
  const [disconnectConfirmVisible, setDisconnectConfirmVisible] = useState(false);
  const [disconnectingAccountId, setDisconnectingAccountId] = useState(null);
  const [defaultConfirmVisible, setDefaultConfirmVisible] = useState(false);
  const [defaultAccountId, setDefaultAccountId] = useState(null);
  const activeAuthWindowRef = useRef(null);
  const activeMessageHandlerRef = useRef(null);
  const activeCheckIntervalRef = useRef(null);
  const [isAuthInProgress, setIsAuthInProgress] = useState(false);
  const [editingField, setEditingField] = useState(null); // 'phone', 'region', 'supervisor', 'userCode', 'vacation', 'resignationDate', 'profileMessage' 중 하나
  const avatarInputRef = useRef(null);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [cropFile, setCropFile] = useState(null);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarPreviewOpen, setAvatarPreviewOpen] = useState(false);
  const [tempFieldValue, setTempFieldValue] = useState(''); // 임시 필드 값
  const [supervisorModalVisible, setSupervisorModalVisible] = useState(false); // 상위관리자 선택 모달 표시 여부
  const [tempSupervisorId, setTempSupervisorId] = useState(null); // 임시 상위관리자 ID
  const [tempSupervisorName, setTempSupervisorName] = useState(''); // 임시 상위관리자 이름
  const [tempSupervisorEmail, setTempSupervisorEmail] = useState(''); // 임시 상위관리자 이메일 (부서 미지정 사용자용)
  const [originalSupervisorId, setOriginalSupervisorId] = useState(null); // 원래 상위관리자 ID (취소 시 복원용)
  const [originalSupervisorName, setOriginalSupervisorName] = useState(''); // 원래 상위관리자 이름 (취소 시 복원용)
  const [originalSupervisorEmail, setOriginalSupervisorEmail] = useState(''); // 원래 상위관리자 이메일 (취소 시 복원용)
  const [passwordModalVisible, setPasswordModalVisible] = useState(false); // 비밀번호 변경 모달 표시 여부
  const [vacationDateMode, setVacationDateMode] = useState('preset1'); // preset1 | preset2 | custom
  const [tempVacationStartDate, setTempVacationStartDate] = useState(''); // 임시 휴가 시작일
  const [tempVacationEndDate, setTempVacationEndDate] = useState(''); // 임시 휴가 종료일
  const [availableSubstituteUsers, setAvailableSubstituteUsers] = useState([]); // 대직 가능한 사용자 목록
  const [substituteModalVisible, setSubstituteModalVisible] = useState(false); // 대직 담당자 선택 모달 표시 여부
  const [tempSubstituteUserId, setTempSubstituteUserId] = useState(null); // 임시 대직 담당자 ID
  const [userHasPermission, setUserHasPermission] = useState(false); // 사용자가 권한이 있는지 여부
  const [editingInsuranceTypes, setEditingInsuranceTypes] = useState(false); // 보험 타입 수정 모드
  const [tempIsCarInsu, setTempIsCarInsu] = useState(false); // 임시 자동차보험 선택
  const [tempIsGeneralInsu, setTempIsGeneralInsu] = useState(false); // 임시 일반보험 선택
  const [tempIsNonlifeInsu, setTempIsNonlifeInsu] = useState(false); // 임시 장기보험 선택
  const [tempIsLifeInsu, setTempIsLifeInsu] = useState(false); // 임시 생명보험 선택
  // 부서 저장 후 응답으로 화면 즉시 반영 (부서장 여부 등). 부서 변경/제거 시 서버가 isDeptLeader false 반환.
  const [lastProfileSaveForDisplay, setLastProfileSaveForDisplay] = useState(null);
  const [isOrg, setIsOrg] = useState(null);
  const [isOrgLoading, setIsOrgLoading] = useState(false);
  const [assignedWorkplaceModalVisible, setAssignedWorkplaceModalVisible] = useState(false);

  const clearTempSupervisor = () => {
    setTempSupervisorId(null);
    setTempSupervisorName('');
    setTempSupervisorEmail('');
  };

  const handleSelectTempSubstituteUser = (userId) => {
    // 대직자 변경 시점에는 저장 안내 알림을 띄우지 않는다.
    setTempSubstituteUserId(userId || null);
  };

  const hasVacationSubstituteTempChanges = useMemo(() => {
    if (!visible) return false;
    if (editingField !== 'vacation') return false;
    return (
      (tempVacationStartDate || '') !== (user?.vacationStartDate || '') ||
      (tempVacationEndDate || '') !== (user?.vacationEndDate || '') ||
      (tempSubstituteUserId || null) !== (user?.substituteUserId || null)
    );
  }, [
    visible,
    editingField,
    tempVacationStartDate,
    tempVacationEndDate,
    tempSubstituteUserId,
    user?.vacationStartDate,
    user?.vacationEndDate,
    user?.substituteUserId
  ]);

  // 새로고침/탭닫기 등 이탈 방지 (브라우저 기본 확인창)
  useEffect(() => {
    if (!hasVacationSubstituteTempChanges) return;

    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasVacationSubstituteTempChanges]);

  // division 스코프 정책
  // - OP(운영사): 선택된/대상 사용자 division 기준(운영사가 여러 GA 사업장을 관리)
  // - GA/IS: 로그인 사용자 division 기준(동일 사업장 스코프)
  const selectionDivisionId =
    currentUser?.companyType === 'OP'
      ? (user?.divisionId ?? null)
      : (currentUser?.divisionId ?? user?.divisionId ?? null);

  const anyGoogleCalendarConnected = useMemo(
    () => googleAccounts.some((a) => a.isConnected),
    [googleAccounts]
  );

  const anyGoogleNeedsReauth = useMemo(
    () => googleAccounts.some((a) => a.needsReauth),
    [googleAccounts]
  );

  useLayoutEffect(() => {
    if (visible && user) {
      setGoogleAccountsLoading(true);
    }
  }, [visible, user]);

  useEffect(() => {
    if (visible && user) {
      setLastProfileSaveForDisplay(null); // 모달 열릴 때/user 변경 시 초기화
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        regionId: user.regionId || null,
        supervisorEmail: user.supervisorEmail || '',
        departmentId: user.departmentId || null,
        googleCalendarSyncEnabled: user.googleCalendarSyncEnabled || false,
        googleEmail: user.googleEmail || '',
        userCode: user.userCode || '',
        vacationStartDate: user.vacationStartDate || '',
        vacationEndDate: user.vacationEndDate || '',
      });
      fetchGoogleAccounts();
      fetchRegions();
      if (user?.companyType === 'GA') {
        fetchCompanies();
        fetchAgentCodes();
      }
      // GA 또는 IS 타입 사용자는 대직 담당자 후보 조회 및 권한 확인
      if (user?.companyType === 'GA' || user?.companyType === 'IS') {
        fetchAvailableSubstituteUsersForProfile(); // 대직 담당자 후보 목록 조회
        checkUserPermissionsForSubstitute(); // 사용자 권한 확인
      }
      if (user?.companyType === 'IS') {
        fetchIsOrg();
      } else {
        setIsOrg(null);
        setAssignedWorkplaceModalVisible(false);
      }
      // 휴가 기간 방식 (표시용; 편집 시에는 수정 버튼에서 다시 맞춤)
      if (user?.vacationStartDate && user?.vacationEndDate) {
        const span = ymdSpanDays(user.vacationStartDate, user.vacationEndDate);
        if (span === 0) setVacationDateMode('preset1');
        else if (span === 1) setVacationDateMode('preset2');
        else setVacationDateMode('custom');
      } else {
        setVacationDateMode('preset1');
      }
      // 대직 담당자 초기값 설정
      setTempSubstituteUserId(user?.substituteUserId || null);
      // 보험 타입 초기값 설정
      setTempIsCarInsu(user?.isCarInsu || false);
      setTempIsGeneralInsu(user?.isGeneralInsu || false);
      setTempIsNonlifeInsu(user?.isNonlifeInsu || false);
      setTempIsLifeInsu(user?.isLifeInsu || false);
    }

    // 컴포넌트 언마운트 시 정리
    return () => {
      try {
        if (activeAuthWindowRef.current && !activeAuthWindowRef.current.closed) {
          activeAuthWindowRef.current.close();
        }
      } catch (error) {
        // COOP 정책으로 인한 접근 차단 시 무시
        console.debug('창 닫기 중 오류 (COOP 정책):', error);
      }
      if (activeMessageHandlerRef.current) {
        window.removeEventListener('message', activeMessageHandlerRef.current);
      }
      if (activeCheckIntervalRef.current) {
        clearInterval(activeCheckIntervalRef.current);
      }
      setIsAuthInProgress(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- visible, user 변경 시에만 실행
  }, [visible, user]);

  useEffect(() => {
    if (!visible) {
      setAvatarPreviewOpen(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible || !user?.hasAvatar) {
      setAvatarUrl('');
      return undefined;
    }
    let objectUrl = '';
    let cancelled = false;
    api.get(API_ENDPOINTS.USER_PROFILE_AVATAR, { responseType: 'blob' })
      .then((response) => {
        const url = URL.createObjectURL(response.data);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setAvatarUrl(url);
      })
      .catch(() => {
        if (!cancelled) setAvatarUrl('');
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [visible, user?.hasAvatar, user?.updatedAt]);

  const fetchIsOrg = async () => {
    setIsOrgLoading(true);
    try {
      const response = await api.get(API_ENDPOINTS.USER_PROFILE_IS_ORG);
      setIsOrg(response?.data || { workplaces: [] });
    } catch (error) {
      console.error('조직·배정사업장 조회 실패:', error);
      setIsOrg(null);
    } finally {
      setIsOrgLoading(false);
    }
  };

  // 구글 계정 목록 조회
  const fetchGoogleAccounts = async () => {
    try {
      const response = await api.get('/api/google-accounts');
      if (response && response.data) {
        setGoogleAccounts(response.data);
      } else {
        setGoogleAccounts([]);
      }
    } catch (error) {
      console.error('구글 계정 목록 조회 실패:', error);
      setGoogleAccounts([]);
    } finally {
      setGoogleAccountsLoading(false);
    }
  };

  // 활성 지역 목록 조회
  const fetchRegions = async () => {
    try {
      const response = await api.get('/api/regions');
      if (response && response.data) {
        setRegions(response.data);
      }
    } catch (error) {
      console.error('지역 목록 조회 실패:', error);
    }
  };

  // 활성화된 IS 타입 회사 목록 조회
  const fetchCompanies = async () => {
    try {
      // GA 타입 사용자는 /api/companies/available 엔드포인트 사용
      const response = await api.get('/api/companies/available');
      if (response && response.data) {
        // IS 타입이고 활성화된 회사만 필터링 (이미 백엔드에서 필터링되지만 안전을 위해)
        const isCompanies = response.data.filter(
          company => company.companyType === 'IS' && company.isActive
        );
        setCompanies(isCompanies);
      }
    } catch (error) {
      console.error('회사 목록 조회 실패:', error);
      if (error.response?.status === 403) {
        console.error('권한이 없습니다. GA 타입 사용자만 접근 가능합니다.');
      }
    }
  };

  // 설계사코드 목록 조회
  const fetchAgentCodes = async () => {
    try {
      const response = await api.get('/api/agent-codes/my-codes');
      if (response && response.data) {
        setAgentCodes(response.data);
      }
    } catch (error) {
      console.error('설계사코드 목록 조회 실패:', error);
    }
  };

  // 대직 가능한 사용자 목록 조회 (프로필 수정용)
  const fetchAvailableSubstituteUsersForProfile = async () => {
    try {
      const response = await api.get('/api/users/profile/substitute-candidates');
      setAvailableSubstituteUsers(response.data || []);
    } catch (error) {
      console.error('대직 가능한 사용자 목록 조회 실패:', error);
      showAlert(error.response?.data?.error || '대직 담당자 목록을 불러오는데 실패했습니다.');
      setAvailableSubstituteUsers([]);
    }
  };

  // 현재 사용자의 대직 관련 권한 확인
  const checkUserPermissionsForSubstitute = () => {
    if (user?.permissions) {
      const hasDesignPermission = user.permissions.includes('DESIGN_RECEIVE_CREATE');
      const hasSubscriptionPermission = user.permissions.includes('SUBSCRIPTION_RECEIVE_CREATE');
      setUserHasPermission(hasDesignPermission || hasSubscriptionPermission);
    } else {
      setUserHasPermission(false);
    }
  };


  // 정렬된 설계사코드 목록
  const sortedAgentCodes = useMemo(() => {
    if (!agentCodes || agentCodes.length === 0) return [];

    const sorted = [...agentCodes];
    if (agentCodeSortOrder === 'korean') {
      // 가나다순 정렬 (한국어)
      sorted.sort((a, b) => {
        return (a.companyName || '').localeCompare(b.companyName || '', 'ko');
      });
    } else {
      // 알파벳순 정렬
      sorted.sort((a, b) => {
        return (a.companyName || '').localeCompare(b.companyName || '', 'en');
      });
    }
    return sorted;
  }, [agentCodes, agentCodeSortOrder]);



  // 개별 필드 수정 시작
  const handleStartEditField = (field, currentValue) => {
    setEditingField(field);
    setTempFieldValue(currentValue || '');
  };

  // 개별 필드 수정 취소
  const handleCancelEditField = async () => {
    if (editingField === 'vacation' && hasVacationSubstituteTempChanges) {
      const confirmed = await showConfirm(
        '휴가일/대직자가 변경되었습니다. \n저장 버튼을 선택하지 않으면 반영되지 않습니다.\n취소하시겠습니까?'
      );
      if (!confirmed) return;
    }
    setEditingField(null);
    setTempFieldValue('');
    setTempVacationStartDate('');
    setTempVacationEndDate('');
    setVacationDateMode('preset1');
    setTempSupervisorId(null);
    setTempSupervisorName('');
    setTempSupervisorEmail('');
    setSupervisorModalVisible(false);
    setTempSubstituteUserId(null);
    setSubstituteModalVisible(false);
    setEditingInsuranceTypes(false);
    setTempIsCarInsu(user?.isCarInsu || false);
    setTempIsGeneralInsu(user?.isGeneralInsu || false);
    setTempIsNonlifeInsu(user?.isNonlifeInsu || false);
    setTempIsLifeInsu(user?.isLifeInsu || false);
  };

  const handleCloseModal = () => {
    setEditingField(null);
    setTempFieldValue('');
    setTempVacationStartDate('');
    setTempVacationEndDate('');
    setVacationDateMode('preset1');
    setTempSupervisorId(null);
    setTempSupervisorName('');
    setTempSupervisorEmail('');
    setSupervisorModalVisible(false);
    setTempSubstituteUserId(null);
    setSubstituteModalVisible(false);
    setEditingInsuranceTypes(false);
    setTempIsCarInsu(user?.isCarInsu || false);
    setTempIsGeneralInsu(user?.isGeneralInsu || false);
    setTempIsNonlifeInsu(user?.isNonlifeInsu || false);
    setTempIsLifeInsu(user?.isLifeInsu || false);
    setAssignedWorkplaceModalVisible(false);
    onClose();
  };

  // 개별 필드 저장
  const handleSaveField = async (field) => {
    try {
      setLoading(true);
      const updateData = {};

      if (field === 'phone') {
        // 휴대폰번호 유효성 검사
        if (tempFieldValue && !/^[0-9-+\s()]+$/.test(tempFieldValue)) {
          showAlert('올바른 휴대폰번호 형식이 아닙니다!');
          return;
        }
        updateData.phone = tempFieldValue || null;
      } else if (field === 'region') {
        updateData.regionId = tempFieldValue ? parseInt(tempFieldValue) : null;
      } else if (field === 'supervisor') {
        // 상위관리자 설정
        // 계층트리에서 선택한 경우 supervisorId 사용, 이메일 직접 입력한 경우 supervisorEmail 사용
        if (tempSupervisorId) {
          updateData.supervisorId = tempSupervisorId;
          updateData.supervisorEmail = null;
        } else if (tempSupervisorEmail && tempSupervisorEmail.trim()) {
          // 이메일 유효성 검사
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(tempSupervisorEmail.trim())) {
            showAlert('올바른 이메일 형식이 아닙니다!');
            setLoading(false);
            return;
          }
          updateData.supervisorId = null;
          updateData.supervisorEmail = tempSupervisorEmail.trim();
        } else {
          updateData.supervisorId = null;
          updateData.supervisorEmail = null;
        }
      } else if (field === 'userCode') {
        // userCode 유효성 검사 (unique이므로 빈 값도 허용)
        updateData.userCode = tempFieldValue && tempFieldValue.trim() ? tempFieldValue.trim() : null;
      } else if (field === 'vacation') {
        // 휴가일 설정
        if (tempVacationStartDate && tempVacationEndDate) {
          updateData.vacationStartDate = tempVacationStartDate;
          updateData.vacationEndDate = tempVacationEndDate;
          // 시작일 변경 시점에는 기존 대직자를 초기화하지만, 이후 다시 선택한 대직자는 저장한다.
          updateData.substituteUserId = tempSubstituteUserId || null;
        } else {
          // 휴가일 삭제 시 대직 담당자도 함께 삭제
          updateData.vacationStartDate = null;
          updateData.vacationEndDate = null;
          updateData.substituteUserId = null;
        }
      } else if (field === 'resignationDate') {
        updateData.resignationDate = tempFieldValue || null;
      } else if (field === 'profileMessage') {
        const message = (tempFieldValue || '').trim();
        if (message.length > PROFILE_MESSAGE_MAX_LENGTH) {
          showAlert('문구는 30자 이하여야 합니다.');
          setLoading(false);
          return;
        }
        updateData.profileMessage = message;
      } else if (field === 'insuranceTypes') {
        // 보험 타입 설정 - 네 가지 중 하나는 반드시 선택되어야 함
        if (!tempIsCarInsu && !tempIsGeneralInsu && !tempIsNonlifeInsu && !tempIsLifeInsu) {
          showAlert('자동차보험, 일반보험, 장기보험, 생명보험 중 최소 하나는 선택해야 합니다.');
          setLoading(false);
          return;
        }
        updateData.isCarInsu = tempIsCarInsu;
        updateData.isGeneralInsu = tempIsGeneralInsu;
        updateData.isNonlifeInsu = tempIsNonlifeInsu;
        updateData.isLifeInsu = tempIsLifeInsu;
      }

      const endpoint = field === 'resignationDate'
        ? API_ENDPOINTS.USER_PROFILE_RESIGNATION_DATE
        : API_ENDPOINTS.USER_PROFILE;
      const response = await api.put(endpoint, updateData);

      if (response && response.data) {
        const updatedUser = response.data;
        onUpdate(updatedUser);
        // 부서 변경/제거 시 서버가 isDeptLeader false 반환 → 화면에 즉시 반영
        setLastProfileSaveForDisplay(updatedUser);
        const fieldNames = {
          'phone': '휴대폰번호',
          'region': '지역',
          'supervisor': '상위관리자',
          'userCode': '사용자 코드',
          'vacation': '휴가일',
          'resignationDate': '퇴사(예정)일',
          'insuranceTypes': '보험 타입',
          'profileMessage': '문구'
        };
        showAlert(`${fieldNames[field] || field}이(가) 성공적으로 업데이트되었습니다.`);
        setEditingField(null);
        setTempFieldValue('');
        setTempVacationStartDate('');
        setTempVacationEndDate('');
        setVacationDateMode('preset1');
        setTempSubstituteUserId(null);
        setSubstituteModalVisible(false);
        setTempSupervisorId(null);
        setTempSupervisorName('');
        setTempSupervisorEmail('');
        setSupervisorModalVisible(false);
        if (field === 'insuranceTypes') {
          setEditingInsuranceTypes(false);
        }
      } else {
        showAlert('업데이트에 실패했습니다.');
      }
    } catch (error) {
      console.error('필드 업데이트 실패:', error);
      const errorMessage = error.response?.data?.error || error.message || '업데이트 중 오류가 발생했습니다.';
      // 백엔드에서 오는 에러 메시지를 그대로 표시
      showAlert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > AVATAR_MAX_BYTES) {
      showAlert('프로필 사진은 10MB 이하만 업로드할 수 있습니다.');
      return;
    }
    const type = (file.type || '').toLowerCase();
    if (type && type !== 'image/jpeg' && type !== 'image/jpg' && type !== 'image/png') {
      showAlert('프로필 사진은 PNG 또는 JPG 형식만 지원합니다.');
      return;
    }
    setCropFile(file);
  };

  const handleAvatarCropConfirm = async (croppedFile) => {
    setCropFile(null);
    try {
      setAvatarSaving(true);
      const formData = new FormData();
      formData.append('file', croppedFile);
      const response = await api.post(API_ENDPOINTS.USER_PROFILE_AVATAR, formData, {
        timeout: 120000,
      });
      if (response?.data) {
        onUpdate(response.data);
        showAlert('프로필 사진이 저장되었습니다.');
      }
    } catch (error) {
      showAlert(error.response?.data?.error || '프로필 사진 저장에 실패했습니다.');
    } finally {
      setAvatarSaving(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!user?.hasAvatar) return;
    const confirmed = await showConfirm('프로필 사진을 삭제하고 기본 이미지로 되돌리시겠습니까?');
    if (!confirmed) return;
    try {
      setAvatarSaving(true);
      const response = await api.delete(API_ENDPOINTS.USER_PROFILE_AVATAR);
      if (response?.data) {
        onUpdate(response.data);
        showAlert('프로필 사진을 기본 이미지로 변경했습니다.');
      }
    } catch (error) {
      showAlert(error.response?.data?.error || '프로필 사진 삭제에 실패했습니다.');
    } finally {
      setAvatarSaving(false);
    }
  };

  // 휴가일 삭제 (대직자 정보 포함)
  const handleDeleteVacation = async () => {
    if (!user?.vacationStartDate && !user?.vacationEndDate) return;
    const confirmed = await showConfirm('저장된 휴가일을 삭제하시겠습니까? 해당 휴가일에 지정된 대직자 정보도 함께 삭제됩니다.');
    if (!confirmed) return;
    try {
      setLoading(true);
      const updateData = {
        vacationStartDate: null,
        vacationEndDate: null,
        substituteUserId: null
      };
      const response = await api.put('/api/users/profile', updateData);
      if (response?.data) {
        onUpdate(response.data);
        setLastProfileSaveForDisplay(response.data);
        showAlert('휴가 기간이 변경되면 대직 담당자가 초기화됩니다. \n필요 시 대직 담당자를 다시 선택해주세요.');
        setEditingField(null);
        setTempVacationStartDate('');
        setTempVacationEndDate('');
        setVacationDateMode('preset1');
        setTempSubstituteUserId(null);
        setSubstituteModalVisible(false);
      } else {
        showAlert('휴가일 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('휴가일 삭제 실패:', error);
      const errorMessage = error.response?.data?.error || error.message || '휴가일 삭제 중 오류가 발생했습니다.';
      showAlert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getUserRoleLabel = (userRole, roleName) => {
    // roleName이 있으면 사용 (DB에서 조회한 권한명)
    // 시스템 초기 세팅에 반드시 있으므로 fallback 불필요
    return roleName || userRole || '';
  };

  /** 프로필에 표시할 역할 라벨 목록 (userRoles 복수 우선, 없으면 userRole/roleName 단일) */
  const getProfileRoleLabels = (u) => {
    if (!u) return [];
    if (u.userRoles && u.userRoles.length > 0) {
      return u.userRoles.map((r) =>
        getUserRoleLabel(
          typeof r === 'object' ? r.userRole : r,
          typeof r === 'object' ? r.roleName : undefined
        )
      ).filter(Boolean);
    }
    const one = getUserRoleLabel(u.userRole, u.roleName);
    return one ? [one] : [];
  };

  if (!visible) return null;

  // 부서 저장 직후에는 서버 응답(부서장 false 등)을 즉시 표시
  const displayUserForDept = lastProfileSaveForDisplay || user;
  const profileRoleLabels = getProfileRoleLabels(user);
  const canShowVacationSection = Array.isArray(user?.permissions)
    && (
      user.permissions.includes('DESIGN_RECEIVE_CREATE') ||
      user.permissions.includes('SUBSCRIPTION_RECEIVE_CREATE')
    );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black bg-opacity-50">
      <div className="mx-4 flex max-h-[90vh] w-full min-w-0 max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HiOutlineUser className="text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">프로필 정보</h3>
            </div>
            <button
              onClick={handleCloseModal}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="min-h-0 min-w-0 overflow-y-auto px-6 pt-4 pb-6">
          <div className="min-w-0">
            <div className="overflow-x-hidden sm:overflow-x-auto">
              <table className="w-full min-w-0 sm:min-w-[640px] border-collapse max-sm:divide-y-0 divide-y divide-gray-300 border-x border-y-0 border-gray-300 sm:border sm:border-gray-300 rounded-lg" style={{ tableLayout: 'fixed', width: '100%' }}>
                  <colgroup className="max-sm:hidden">
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '36%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '36%' }} />
                  </colgroup>
                  <tbody className="max-sm:block max-sm:divide-y-0 bg-white divide-y divide-gray-300">
                    <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:border-t max-sm:border-gray-300 max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                      <td className="max-sm:block px-3 py-0.5 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">사진</td>
                      <td className="max-sm:block max-sm:min-w-0 px-4 py-0.5 text-sm text-gray-900">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setAvatarPreviewOpen(true)}
                            className="rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            title="프로필 사진 크게 보기"
                          >
                            {avatarUrl ? (
                              <img
                                src={avatarUrl}
                                alt="프로필 사진"
                                className="w-11 h-11 rounded-full object-cover"
                              />
                            ) : (
                              <DefaultAvatar className="w-11 h-11" />
                            )}
                          </button>
                          <div className="flex flex-wrap items-center gap-2">
                            <input
                              ref={avatarInputRef}
                              type="file"
                              accept={AVATAR_ACCEPT}
                              className="hidden"
                              onChange={handleAvatarFileChange}
                              disabled={avatarSaving}
                            />
                            <button
                              type="button"
                              onClick={() => avatarInputRef.current && avatarInputRef.current.click()}
                              disabled={avatarSaving}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors h-6 disabled:opacity-50"
                            >
                              <HiOutlinePencil />
                              {user?.hasAvatar ? '수정' : '등록'}
                            </button>
                            {user?.hasAvatar && (
                              <button
                                type="button"
                                onClick={handleDeleteAvatar}
                                disabled={avatarSaving}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors h-6 disabled:opacity-50"
                              >
                                <HiOutlineTrash />
                                삭제
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="max-sm:block px-3 py-0.5 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">문구</td>
                      <td className="max-sm:block max-sm:min-w-0 px-4 py-0.5 text-sm text-gray-900">
                        {editingField === 'profileMessage' ? (
                          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                            <input
                              type="text"
                              value={tempFieldValue}
                              onChange={(e) => {
                                setTempFieldValue(e.target.value);
                                scrollInputToCaret(e);
                              }}
                              onFocus={scrollInputToCaret}
                              placeholder="문구를 입력하세요"
                              maxLength={PROFILE_MESSAGE_MAX_LENGTH}
                              className="box-border h-6 min-w-0 w-[30ch] max-w-full shrink px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => handleSaveField('profileMessage')}
                              disabled={loading}
                              className="shrink-0 whitespace-nowrap px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 h-6"
                            >
                              저장
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelEditField}
                              disabled={loading}
                              className="shrink-0 whitespace-nowrap px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 h-6"
                            >
                              취소
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="break-words">{user?.profileMessage || '-'}</span>
                            <button
                              type="button"
                              onClick={() => handleStartEditField('profileMessage', user?.profileMessage || '')}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors h-6"
                              title="문구 수정"
                            >
                              <HiOutlinePencil />
                              수정
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                      <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">이름</td>
                      <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900">
                        <div className="flex flex-wrap items-center gap-2">
                          <span>{user?.name}</span>
                          <button
                            onClick={() => setPasswordModalVisible(true)}
                            className="inline-flex items-start gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors min-h-6 whitespace-normal text-left leading-snug"
                            title="비밀번호 변경"
                          >
                            <HiOutlinePencil className="shrink-0 mt-0.5" />
                            비밀번호 변경
                          </button>
                        </div>
                      </td>
                      <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">사용자 코드</td>
                      <td className="max-sm:block px-4 py-3 text-sm text-gray-900 min-w-0">
                        {editingField === 'userCode' ? (
                          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                            <input
                              type="text"
                              value={tempFieldValue}
                              onChange={(e) => {
                                setTempFieldValue(e.target.value);
                                scrollInputToCaret(e);
                              }}
                              onFocus={scrollInputToCaret}
                              placeholder="사용자 코드를 입력하세요"
                              maxLength={15}
                              className="box-border h-6 min-w-0 w-[15ch] max-w-full shrink px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveField('userCode')}
                              disabled={loading}
                              className="shrink-0 whitespace-nowrap px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 h-6"
                            >
                              저장
                            </button>
                            <button
                              onClick={handleCancelEditField}
                              disabled={loading}
                              className="shrink-0 whitespace-nowrap px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 h-6"
                            >
                              취소
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <span>{user?.userCode || '-'}</span>
                            <button
                              onClick={() => handleStartEditField('userCode', user?.userCode || '')}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors h-6"
                              title="사용자 코드 수정"
                            >
                              <HiOutlinePencil />
                              수정
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                      <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">이메일</td>
                      <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900 break-words">{user?.email}</td>
                      <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">휴대폰번호</td>
                      <td className="max-sm:block px-4 py-3 text-sm text-gray-900 min-w-0 break-words">
                        {editingField === 'phone' ? (
                          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                            <input
                              type="text"
                              value={tempFieldValue}
                              onChange={(e) => {
                                setTempFieldValue(e.target.value);
                                scrollInputToCaret(e);
                              }}
                              onFocus={scrollInputToCaret}
                              placeholder="휴대폰번호를 입력하세요"
                              maxLength={16}
                              className="box-border h-6 min-w-0 w-[16ch] max-w-full shrink px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveField('phone')}
                              disabled={loading}
                              className="shrink-0 whitespace-nowrap px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 h-6"
                            >
                              저장
                            </button>
                            <button
                              onClick={handleCancelEditField}
                              disabled={loading}
                              className="shrink-0 whitespace-nowrap px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 h-6"
                            >
                              취소
                            </button>
                          </div>
                        ) : (
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <span className="min-w-0 break-words">{user?.phone || '-'}</span>
                            <button
                              onClick={() => handleStartEditField('phone', user?.phone)}
                              className="inline-flex shrink-0 items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors h-6"
                              title="휴대폰번호 수정"
                            >
                              <HiOutlinePencil />
                              수정
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {user?.companyType === 'GA' ? (
                      <>
                        <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                          <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap align-top">사용자 역할</td>
                          <td className="max-sm:block max-sm:min-w-0 px-4 py-3 align-top">
                            {profileRoleLabels.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {profileRoleLabels.map((label, idx) => (
                                  <span
                                    key={`${label}-${idx}`}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                  >
                                    {label}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">-</span>
                            )}
                          </td>
                          <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">가입일</td>
                          <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900">
                            <span>{user?.createdAt ? dayjs(user.createdAt).format('YYYY-MM-DD') : '-'}</span>
                          </td>
                        </tr>
                        {/* 소속 회사 / 소속 부서 */}
                        {user?.companyName && (
                          <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                            <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">소속 회사</td>
                            <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900">
                              {user.companyType === 'GA' && user.divisionName
                                ? `${user.companyName} (${user.divisionName})`
                                : user.companyName}
                            </td>
                            {user?.companyType === 'GA' ? (
                              <>
                                <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">소속 부서</td>
                                <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="min-w-0 break-words">
                                      {displayUserForDept?.departmentFullName || displayUserForDept?.departmentName || '-'}
                                    </span>
                                    {displayUserForDept?.isDeptLeader && (
                                      <span className="relative inline-flex items-center group">
                                        <HiOutlineBadgeCheck className="w-4 h-4 text-amber-500" aria-hidden />
                                        <span className="absolute left-0 bottom-full mb-1 px-2 py-1 text-xs font-medium text-white bg-gray-800 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible whitespace-nowrap z-10">
                                          해당 부서의 부서장입니다
                                        </span>
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap"></td>
                                <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900"></td>
                              </>
                            )}
                          </tr>
                        )}
                        {/* 상위관리자 - GA이면서 본부장이 아닌 경우만 표시 (맨 아래) */}
                        {(user?.companyType === 'GA' && !user?.isDivisionHead) && (
                          <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                            <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">상위관리자</td>
                            <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900" colSpan="3">
                              {editingField === 'supervisor' ? (
                                <div className="flex flex-wrap items-center gap-2">
                                  {(tempSupervisorId || tempSupervisorName) ? (
                                    <div className="flex-1 flex items-center justify-between px-2 py-1 border border-gray-300 rounded bg-gray-50">
                                      <span className="text-sm text-gray-700 truncate">
                                        {tempSupervisorName || '상위관리자 선택됨'}
                                      </span>
                                      <button
                                        onClick={clearTempSupervisor}
                                        className="text-xs text-red-600 hover:text-red-700 ml-2 flex-shrink-0"
                                      >
                                        제거
                                      </button>
                                    </div>
                                  ) : tempSupervisorEmail ? (
                                    <div className="flex-1 flex items-center justify-between px-2 py-1 border border-gray-300 rounded bg-gray-50">
                                      <span className="text-sm text-gray-700 truncate">
                                        {tempSupervisorEmail}
                                      </span>
                                      <button
                                        onClick={clearTempSupervisor}
                                        className="text-xs text-red-600 hover:text-red-700 ml-2 flex-shrink-0"
                                      >
                                        제거
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex-1 px-2 py-1 border border-gray-300 rounded bg-gray-50 text-sm text-gray-500">
                                      상위관리자 미채택
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // 모달 열기 전 원래 값 저장
                                      setOriginalSupervisorId(tempSupervisorId);
                                      setOriginalSupervisorName(tempSupervisorName);
                                      setOriginalSupervisorEmail(tempSupervisorEmail);
                                      setSupervisorModalVisible(true);
                                    }}
                                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 h-6"
                                  >
                                    {(tempSupervisorId || tempSupervisorName || tempSupervisorEmail) ? '변경' : '선택'}
                                  </button>
                                  <button
                                    onClick={() => handleSaveField('supervisor')}
                                    disabled={loading}
                                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 h-6"
                                  >
                                    저장
                                  </button>
                                  <button
                                    onClick={handleCancelEditField}
                                    disabled={loading}
                                    className="inline-flex shrink-0 items-start px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 min-h-6 whitespace-normal text-left leading-snug"
                                  >
                                    취소
                                  </button>
                                </div>
                              ) : (
                                <div className="flex flex-wrap items-center gap-2">
                                  {user?.supervisorName ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                      {user.supervisorName}
                                    </span>
                                  ) : (
                                    <span>-</span>
                                  )}
                                  <button
                                    onClick={() => {
                                      setEditingField('supervisor');
                                      const supervisorId = user?.supervisorId || null;
                                      const supervisorName = user?.supervisorName || '';
                                      const supervisorEmail = user?.supervisorEmail || '';
                                      setTempSupervisorId(supervisorId);
                                      setTempSupervisorName(supervisorName);
                                      setTempSupervisorEmail(supervisorEmail);
                                      setOriginalSupervisorId(supervisorId);
                                      setOriginalSupervisorName(supervisorName);
                                      setOriginalSupervisorEmail(supervisorEmail);
                                    }}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors h-6"
                                    title="상위관리자 수정"
                                  >
                                    <HiOutlinePencil />
                                    수정
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                        {/* 휴가일 */}
                        {canShowVacationSection && (
                        <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                          <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">휴가일</td>
                          <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900" colSpan="3">
                            {editingField === 'vacation' ? (
                              <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="flex items-center gap-2">
                                    <label className="text-xs text-gray-700">휴가 기간:</label>
                                    <select
                                      value={vacationDateMode}
                                      onChange={(e) => {
                                        const mode = e.target.value;
                                        setVacationDateMode(mode);
                                        if (!tempVacationStartDate) return;
                                        if (mode === 'preset1') {
                                          setTempVacationEndDate(tempVacationStartDate);
                                          setTempSubstituteUserId(null);
                                        } else if (mode === 'preset2') {
                                          setTempVacationEndDate(addDaysToYmd(tempVacationStartDate, 1));
                                          setTempSubstituteUserId(null);
                                        }
                                      }}
                                      className="w-[7.5rem] px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 h-6"
                                    >
                                      <option value="preset1">1일</option>
                                      <option value="preset2">2일 (연속)</option>
                                      <option value="custom">직접 선택</option>
                                    </select>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <label className="text-xs text-gray-700">시작일:</label>
                                    <input
                                      type="date"
                                      value={tempVacationStartDate}
                                      onChange={(e) => {
                                        const startDate = e.target.value;
                                        const prevStart = tempVacationStartDate || '';
                                        const prevHadSubstitute = !!tempSubstituteUserId;
                                        const userHadSubstitute = !!user?.substituteUserId;

                                        setTempVacationStartDate(startDate);

                                        if (startDate !== prevStart) {
                                          if (prevHadSubstitute || userHadSubstitute) {
                                            setTempSubstituteUserId(null);
                                            showAlert('휴가 기간이 변경되면 대직 담당자가 초기화됩니다. 필요 시 대직 담당자를 다시 선택해주세요.');
                                          } else {
                                            setTempSubstituteUserId(null);
                                          }
                                        }

                                        if (!startDate) {
                                          setTempVacationEndDate('');
                                        } else if (vacationDateMode === 'preset1') {
                                          setTempVacationEndDate(startDate);
                                        } else if (vacationDateMode === 'preset2') {
                                          setTempVacationEndDate(addDaysToYmd(startDate, 1));
                                        } else if (tempVacationEndDate && tempVacationEndDate < startDate) {
                                          setTempVacationEndDate(startDate);
                                        }
                                      }}
                                      className="w-32 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 h-6"
                                      autoFocus
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <label className="text-xs text-gray-700">종료일:</label>
                                    <input
                                      type="date"
                                      value={tempVacationEndDate}
                                      onChange={(e) => {
                                        const endDate = e.target.value;
                                        setTempVacationEndDate(endDate);
                                        if (!endDate && !tempVacationStartDate) {
                                          setTempSubstituteUserId(null);
                                        }
                                      }}
                                      min={tempVacationStartDate || undefined}
                                      className={`w-32 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 h-6 ${
                                        vacationDateMode !== 'custom' || !tempVacationStartDate ? 'bg-gray-100 cursor-not-allowed' : ''
                                      }`}
                                      disabled={vacationDateMode !== 'custom' || !tempVacationStartDate}
                                    />
                                  </div>
                                </div>

                                {/* 대직 담당자 선택 (권한이 있는 경우만) */}
                                {userHasPermission && (
                                  <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <label className="text-xs text-gray-700">대직 담당자:</label>
                                    <div className="flex items-center gap-2 w-72 max-w-full">
                                      {tempSubstituteUserId ? (
                                        <div className="w-44 flex items-center justify-between px-2 py-1 border border-gray-300 rounded bg-gray-50">
                                          <span className="text-xs text-gray-700 truncate">
                                            {availableSubstituteUsers.find(u => u.id === tempSubstituteUserId)?.name || user?.substituteUserName || '대직 담당자'}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={() => setTempSubstituteUserId(null)}
                                            className="text-xs text-red-600 hover:text-red-700 ml-2 flex-shrink-0"
                                          >
                                            제거
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="w-44 px-2 py-1 border border-gray-300 rounded bg-gray-50 text-xs text-gray-500">
                                          대직 담당자 없음
                                        </div>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => setSubstituteModalVisible(true)}
                                        disabled={!tempVacationStartDate || !tempVacationEndDate}
                                        className={`inline-flex shrink-0 items-start px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-6 whitespace-normal text-left leading-snug ${
                                          (!tempVacationStartDate || !tempVacationEndDate) ? 'bg-gray-400' : ''
                                        }`}
                                      >
                                        {tempSubstituteUserId ? '변경' : '선택'}
                                      </button>
                                    </div>
                                  </div>
                                )}
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  <span className="text-xs text-red-600">변경사항은 저장버튼을 선택하여야 반영됩니다.</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // 휴가일 삭제 (둘 다 비어있는 경우)
                                      if (!tempVacationStartDate && !tempVacationEndDate) {
                                        handleSaveField('vacation');
                                        return;
                                      }
                                      // 유효성 검사
                                      if (!tempVacationStartDate || !tempVacationEndDate) {
                                        showAlert('휴가 시작일과 종료일을 모두 입력해주세요.');
                                        return;
                                      }
                                      if (new Date(tempVacationStartDate) > new Date(tempVacationEndDate)) {
                                        showAlert('휴가 시작일은 종료일보다 늦을 수 없습니다.');
                                        return;
                                      }
                                      handleSaveField('vacation');
                                    }}
                                    disabled={loading}
                                    className="inline-flex shrink-0 items-start px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 min-h-6 whitespace-normal text-left leading-snug"
                                  >
                                    휴가/대직 저장
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleCancelEditField}
                                    disabled={loading}
                                    className="inline-flex shrink-0 items-start px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 min-h-6 whitespace-normal text-left leading-snug"
                                  >
                                    휴가/대직 취소
                                  </button>
                                  {user?.vacationStartDate && user?.vacationEndDate && (
                                    <button
                                      type="button"
                                      onClick={handleDeleteVacation}
                                      disabled={loading}
                                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50 h-6"
                                      title="휴가일/대직 담당자 삭제"
                                    >
                                      <HiOutlineTrash />
                                      휴가/대직 삭제
                                    </button>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-wrap items-center gap-2">
                                {user?.vacationStartDate && user?.vacationEndDate ? (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span>
                                      {dayjs(user.vacationStartDate).format('YYYY-MM-DD')} ~ {dayjs(user.vacationEndDate).format('YYYY-MM-DD')}
                                    </span>
                                    {(() => {
                                      const today = new Date();
                                      today.setHours(0, 0, 0, 0);
                                      const endDate = new Date(user.vacationEndDate);
                                      endDate.setHours(0, 0, 0, 0);
                                      const startDate = new Date(user.vacationStartDate);
                                      startDate.setHours(0, 0, 0, 0);
                                      // 휴가 종료일이 지난 경우 표시하지 않음
                                      if (endDate < today) {
                                        return null;
                                      }

                                      // 휴가 중인지 확인
                                      const isActive = endDate >= today && startDate <= today;
                                      return (
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                          isActive
                                            ? 'bg-orange-100 text-orange-800'
                                            : 'bg-blue-100 text-blue-800'
                                        }`}>
                                          {isActive ? '휴가중' : '휴가예정'}
                                        </span>
                                      );
                                    })()}
                                    {/* 대직 담당자 정보 표시 */}
                                    {user?.substituteUserName ? (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        대직: {user.substituteUserName}
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                        대직담당자 없음
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span>-</span>
                                )}
                                <button
                                  onClick={() => {
                                    setEditingField('vacation');
                                    if (user?.vacationStartDate && user?.vacationEndDate) {
                                      setTempVacationStartDate(user.vacationStartDate);
                                      setTempVacationEndDate(user.vacationEndDate);
                                      const span = ymdSpanDays(user.vacationStartDate, user.vacationEndDate);
                                      if (span === 0) setVacationDateMode('preset1');
                                      else if (span === 1) setVacationDateMode('preset2');
                                      else setVacationDateMode('custom');
                                      setTempSubstituteUserId(user?.substituteUserId || null);
                                    } else {
                                      setTempVacationStartDate('');
                                      setTempVacationEndDate('');
                                      setVacationDateMode('preset1');
                                      setTempSubstituteUserId(null);
                                    }
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors h-6"
                                  title="휴가일 수정"
                                >
                                  <HiOutlinePencil />
                                  {user?.vacationStartDate && user?.vacationEndDate ? '수정' : '등록'}
                                </button>
                                {user?.vacationStartDate && user?.vacationEndDate && (
                                  <button
                                    onClick={handleDeleteVacation}
                                    disabled={loading}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors h-6 disabled:opacity-50"
                                    title="휴가일 삭제"
                                  >
                                    <HiOutlineTrash />
                                    삭제
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                        )}
                        {/* 보험 타입 선택 - GA 타입이면서 DESIGN_RECEIVE_CREATE 권한이 있는 경우만 표시 */}
                        {user?.companyType === 'GA' && user?.permissions?.includes('DESIGN_RECEIVE_CREATE') && (
                          <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                            <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">보험 처리 유형</td>
                            <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900" colSpan="3">
                              {editingInsuranceTypes ? (
                                <div className="space-y-2">
                                  <div className="flex flex-wrap items-center gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={tempIsCarInsu}
                                        onChange={(e) => setTempIsCarInsu(e.target.checked)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                      />
                                      <span className="text-sm text-gray-700">{INSURANCE_TYPE_DISPLAY_NAME_MAP[INSURANCE_TYPE.CAR]}</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={tempIsGeneralInsu}
                                        onChange={(e) => setTempIsGeneralInsu(e.target.checked)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                      />
                                      <span className="text-sm text-gray-700">{INSURANCE_TYPE_DISPLAY_NAME_MAP[INSURANCE_TYPE.GENERAL]}</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={tempIsNonlifeInsu}
                                        onChange={(e) => setTempIsNonlifeInsu(e.target.checked)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                      />
                                      <span className="text-sm text-gray-700">{INSURANCE_TYPE_DISPLAY_NAME_MAP[INSURANCE_TYPE.NONLIFE]}</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={tempIsLifeInsu}
                                        onChange={(e) => setTempIsLifeInsu(e.target.checked)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                      />
                                      <span className="text-sm text-gray-700">{INSURANCE_TYPE_DISPLAY_NAME_MAP[INSURANCE_TYPE.LIFE]}</span>
                                    </label>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleSaveField('insuranceTypes')}
                                      disabled={loading}
                                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 h-6"
                                    >
                                      저장
                                    </button>
                                    <button
                                      onClick={handleCancelEditField}
                                      disabled={loading}
                                      className="inline-flex shrink-0 items-start px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 min-h-6 whitespace-normal text-left leading-snug"
                                    >
                                      취소
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="flex items-center gap-4">
                                    {user?.isCarInsu && (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                        {INSURANCE_TYPE_DISPLAY_NAME_MAP[INSURANCE_TYPE.CAR]}
                                      </span>
                                    )}
                                    {user?.isGeneralInsu && (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                        {INSURANCE_TYPE_DISPLAY_NAME_MAP[INSURANCE_TYPE.GENERAL]}
                                      </span>
                                    )}
                                    {user?.isNonlifeInsu && (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {INSURANCE_TYPE_DISPLAY_NAME_MAP[INSURANCE_TYPE.NONLIFE]}
                                      </span>
                                    )}
                                    {user?.isLifeInsu && (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        {INSURANCE_TYPE_DISPLAY_NAME_MAP[INSURANCE_TYPE.LIFE]}
                                      </span>
                                    )}
                                    {!user?.isCarInsu && !user?.isGeneralInsu && !user?.isNonlifeInsu && !user?.isLifeInsu && (
                                      <span className="text-sm text-gray-500">선택된 보험 타입이 없습니다</span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingInsuranceTypes(true);
                                      setTempIsCarInsu(user?.isCarInsu || false);
                                      setTempIsGeneralInsu(user?.isGeneralInsu || false);
                                      setTempIsNonlifeInsu(user?.isNonlifeInsu || false);
                                      setTempIsLifeInsu(user?.isLifeInsu || false);
                                    }}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors h-6"
                                    title="보험 타입 수정"
                                  >
                                    <HiOutlinePencil />
                                    수정
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    ) : (
                      <>
                        <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                          <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap align-top">사용자 역할</td>
                          <td className="max-sm:block max-sm:min-w-0 px-4 py-3 align-top" colSpan={3}>
                            {profileRoleLabels.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {profileRoleLabels.map((label, idx) => (
                                  <span
                                    key={`${label}-nonga-${idx}`}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                  >
                                    {label}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">-</span>
                            )}
                          </td>
                        </tr>
                        {user?.companyName ? (
                          <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                            <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">소속 회사</td>
                            <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900">
                              {user.companyType === 'GA' && user.divisionName
                                ? `${user.companyName} (${user.divisionName})`
                                : user.companyName}
                            </td>
                            <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">가입일</td>
                            <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900">
                              <span>{user?.createdAt ? dayjs(user.createdAt).format('YYYY-MM-DD') : '-'}</span>
                            </td>
                          </tr>
                        ) : (
                          <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                            <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">가입일</td>
                            <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900" colSpan="3">
                              <span>{user?.createdAt ? dayjs(user.createdAt).format('YYYY-MM-DD') : '-'}</span>
                            </td>
                          </tr>
                        )}
                        {user?.companyType === 'IS' && (
                          <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                            <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">소속 조직</td>
                            <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900">
                              <span className="min-w-0 break-words">
                                {isOrgLoading ? '조회 중...' : (isOrg?.orgPath || '-')}
                              </span>
                            </td>
                            <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">배정사업장</td>
                            <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900">
                              <button
                                type="button"
                                onClick={() => setAssignedWorkplaceModalVisible(true)}
                                className="inline-flex shrink-0 items-center px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors h-6"
                              >
                                배정사업장조회
                              </button>
                            </td>
                          </tr>
                        )}
                        {/* 휴가일 */}
                        {canShowVacationSection && (
                        <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                          <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">휴가일</td>
                          <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900" colSpan="3">
                            {editingField === 'vacation' ? (
                              <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="flex items-center gap-2">
                                    <label className="text-xs text-gray-700">휴가 기간:</label>
                                    <select
                                      value={vacationDateMode}
                                      onChange={(e) => {
                                        const mode = e.target.value;
                                        setVacationDateMode(mode);
                                        if (!tempVacationStartDate) return;
                                        if (mode === 'preset1') {
                                          setTempVacationEndDate(tempVacationStartDate);
                                          setTempSubstituteUserId(null);
                                        } else if (mode === 'preset2') {
                                          setTempVacationEndDate(addDaysToYmd(tempVacationStartDate, 1));
                                          setTempSubstituteUserId(null);
                                        }
                                      }}
                                      className="w-[7.5rem] px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 h-6"
                                    >
                                      <option value="preset1">1일</option>
                                      <option value="preset2">2일 (연속)</option>
                                      <option value="custom">직접 선택</option>
                                    </select>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <label className="text-xs text-gray-700">시작일:</label>
                                    <input
                                      type="date"
                                      value={tempVacationStartDate}
                                      onChange={(e) => {
                                        const startDate = e.target.value;
                                        const prevStart = tempVacationStartDate || '';
                                        const prevHadSubstitute = !!tempSubstituteUserId;
                                        const userHadSubstitute = !!user?.substituteUserId;

                                        setTempVacationStartDate(startDate);

                                        if (startDate !== prevStart) {
                                          if (prevHadSubstitute || userHadSubstitute) {
                                            setTempSubstituteUserId(null);
                                            showAlert('휴가 기간이 변경되면 대직 담당자가 초기화됩니다. 필요 시 대직 담당자를 다시 선택해주세요.');
                                          } else {
                                            setTempSubstituteUserId(null);
                                          }
                                        }

                                        if (!startDate) {
                                          setTempVacationEndDate('');
                                        } else if (vacationDateMode === 'preset1') {
                                          setTempVacationEndDate(startDate);
                                        } else if (vacationDateMode === 'preset2') {
                                          setTempVacationEndDate(addDaysToYmd(startDate, 1));
                                        } else if (tempVacationEndDate && tempVacationEndDate < startDate) {
                                          setTempVacationEndDate(startDate);
                                        }
                                      }}
                                      className="w-32 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 h-6"
                                      autoFocus
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <label className="text-xs text-gray-700">종료일:</label>
                                    <input
                                      type="date"
                                      value={tempVacationEndDate}
                                      onChange={(e) => {
                                        const endDate = e.target.value;
                                        setTempVacationEndDate(endDate);
                                        if (!endDate && !tempVacationStartDate) {
                                          setTempSubstituteUserId(null);
                                        }
                                      }}
                                      min={tempVacationStartDate || undefined}
                                      className={`w-32 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 h-6 ${
                                        vacationDateMode !== 'custom' || !tempVacationStartDate ? 'bg-gray-100 cursor-not-allowed' : ''
                                      }`}
                                      disabled={vacationDateMode !== 'custom' || !tempVacationStartDate}
                                    />
                                  </div>
                                </div>

                                {/* 대직 담당자 선택 (권한이 있는 경우만) */}
                                {userHasPermission && (
                                  <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <label className="text-xs text-gray-700">대직 담당자:</label>
                                    <div className="flex items-center gap-2 w-72 max-w-full">
                                      {tempSubstituteUserId ? (
                                        <div className="w-44 flex items-center justify-between px-2 py-1 border border-gray-300 rounded bg-gray-50">
                                          <span className="text-xs text-gray-700 truncate">
                                            {availableSubstituteUsers.find(u => u.id === tempSubstituteUserId)?.name || user?.substituteUserName || '대직 담당자'}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={() => setTempSubstituteUserId(null)}
                                            className="text-xs text-red-600 hover:text-red-700 ml-2 flex-shrink-0"
                                          >
                                            제거
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="w-44 px-2 py-1 border border-gray-300 rounded bg-gray-50 text-xs text-gray-500">
                                          대직 담당자 없음
                                        </div>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => setSubstituteModalVisible(true)}
                                        disabled={!tempVacationStartDate || !tempVacationEndDate}
                                        className={`inline-flex shrink-0 items-start px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-6 whitespace-normal text-left leading-snug ${
                                          (!tempVacationStartDate || !tempVacationEndDate) ? 'bg-gray-400' : ''
                                        }`}
                                      >
                                        {tempSubstituteUserId ? '변경' : '선택'}
                                      </button>
                                    </div>
                                  </div>
                                )}
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  <span className="text-xs text-red-600">변경사항은 저장버튼을 선택하여야 반영됩니다.</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (!tempVacationStartDate && !tempVacationEndDate) {
                                        handleSaveField('vacation');
                                        return;
                                      }
                                      if (!tempVacationStartDate || !tempVacationEndDate) {
                                        showAlert('휴가 시작일과 종료일을 모두 입력해주세요.');
                                        return;
                                      }
                                      if (new Date(tempVacationStartDate) > new Date(tempVacationEndDate)) {
                                        showAlert('휴가 시작일은 종료일보다 늦을 수 없습니다.');
                                        return;
                                      }
                                      handleSaveField('vacation');
                                    }}
                                    disabled={loading}
                                    className="inline-flex shrink-0 items-start px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 min-h-6 whitespace-normal text-left leading-snug"
                                  >
                                    휴가/대직 저장
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleCancelEditField}
                                    disabled={loading}
                                    className="inline-flex shrink-0 items-start px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 min-h-6 whitespace-normal text-left leading-snug"
                                  >
                                    휴가/대직 취소
                                  </button>
                                  {user?.vacationStartDate && user?.vacationEndDate && (
                                    <button
                                      type="button"
                                      onClick={handleDeleteVacation}
                                      disabled={loading}
                                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50 h-6"
                                      title="휴가일/대직 담당자 삭제"
                                    >
                                      <HiOutlineTrash />
                                      휴가/대직 삭제
                                    </button>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-wrap items-center gap-2">
                                {user?.vacationStartDate && user?.vacationEndDate ? (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span>
                                      {dayjs(user.vacationStartDate).format('YYYY-MM-DD')} ~ {dayjs(user.vacationEndDate).format('YYYY-MM-DD')}
                                    </span>
                                    {(() => {
                                      const today = new Date();
                                      today.setHours(0, 0, 0, 0);
                                      const endDate = new Date(user.vacationEndDate);
                                      endDate.setHours(0, 0, 0, 0);
                                      const startDate = new Date(user.vacationStartDate);
                                      startDate.setHours(0, 0, 0, 0);
                                      // 휴가 종료일이 지난 경우 표시하지 않음
                                      if (endDate < today) {
                                        return null;
                                      }

                                      // 휴가 중인지 확인
                                      const isActive = endDate >= today && startDate <= today;
                                      return (
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                          isActive
                                            ? 'bg-orange-100 text-orange-800'
                                            : 'bg-blue-100 text-blue-800'
                                        }`}>
                                          {isActive ? '휴가중' : '휴가예정'}
                                        </span>
                                      );
                                    })()}
                                    {/* 대직 담당자 정보 표시 */}
                                    {user?.substituteUserName ? (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        대직: {user.substituteUserName}
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                        대직담당자 없음
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span>-</span>
                                )}
                                <button
                                  onClick={() => {
                                    setEditingField('vacation');
                                    if (user?.vacationStartDate && user?.vacationEndDate) {
                                      setTempVacationStartDate(user.vacationStartDate);
                                      setTempVacationEndDate(user.vacationEndDate);
                                      const span = ymdSpanDays(user.vacationStartDate, user.vacationEndDate);
                                      if (span === 0) setVacationDateMode('preset1');
                                      else if (span === 1) setVacationDateMode('preset2');
                                      else setVacationDateMode('custom');
                                      setTempSubstituteUserId(user?.substituteUserId || null);
                                    } else {
                                      setTempVacationStartDate('');
                                      setTempVacationEndDate('');
                                      setVacationDateMode('preset1');
                                      setTempSubstituteUserId(null);
                                    }
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors h-6"
                                  title="휴가일 수정"
                                >
                                  <HiOutlinePencil />
                                  {user?.vacationStartDate && user?.vacationEndDate ? '수정' : '등록'}
                                </button>
                                {user?.vacationStartDate && user?.vacationEndDate && (
                                  <button
                                    onClick={handleDeleteVacation}
                                    disabled={loading}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors h-6 disabled:opacity-50"
                                    title="휴가일 삭제"
                                  >
                                    <HiOutlineTrash />
                                    삭제
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                        )}
                        {user?.companyType === 'IS' && (
                          <tr className="max-sm:grid max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:[&>td]:border-b max-sm:[&>td]:border-gray-300">
                            <td className="max-sm:block px-3 py-3 text-xs font-medium text-gray-500 bg-gray-200 whitespace-nowrap">
                              퇴사(예정)일
                            </td>
                            <td className="max-sm:block max-sm:min-w-0 px-4 py-3 text-sm text-gray-900" colSpan="3">
                              {editingField === 'resignationDate' ? (
                                <div className="flex flex-wrap items-center gap-2">
                                  <input
                                    type="date"
                                    value={tempFieldValue}
                                    onChange={(e) => setTempFieldValue(e.target.value)}
                                    className="w-36 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 h-6"
                                    autoFocus
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleSaveField('resignationDate')}
                                    disabled={loading}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 h-6"
                                  >
                                    저장
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleCancelEditField}
                                    disabled={loading}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50 h-6"
                                  >
                                    취소
                                  </button>
                                  <span className="text-xs text-gray-500">날짜를 지우고 저장하면 삭제됩니다.</span>
                                </div>
                              ) : (
                                <div className="flex flex-wrap items-center gap-2">
                                  <span>{user?.resignationDate ? dayjs(user.resignationDate).format('YYYY-MM-DD') : '-'}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingField('resignationDate');
                                      setTempFieldValue(user?.resignationDate || '');
                                    }}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors h-6"
                                    title="퇴사(예정)일 수정"
                                  >
                                    <HiOutlinePencil />
                                    {user?.resignationDate ? '수정' : '등록'}
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
            </div>

              {/* 설계사코드 영역 - GA 회원만 표시 (조회 화면) */}
              {user?.companyType === 'GA' && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="bg-white rounded-lg border border-gray-200">
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center gap-2">
                        <HiOutlineUser className="text-gray-600" />
                        <h4 className="text-sm font-semibold text-gray-900">설계사코드 관리</h4>
                      </div>
                    </div>
                    <div className="p-4 space-y-4">
                      {/* 기존 설계사코드 목록 */}
                      {agentCodes.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-sm font-medium text-gray-700">등록된 설계사코드</div>
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-gray-600">정렬:</label>
                              <select
                                value={agentCodeSortOrder}
                                onChange={(e) => setAgentCodeSortOrder(e.target.value)}
                                className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              >
                                <option value="korean">가나다순</option>
                                <option value="alphabet">알파벳순</option>
                              </select>
                            </div>
                          </div>
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* 헤더 - 고정 */}
                            <div className="overflow-x-auto bg-gray-50 border-b border-gray-200">
                              <table className="w-full border-collapse table-fixed">
                                <colgroup>
                                  <col style={{ width: '50%' }} />
                                  <col style={{ width: '50%' }} />
                                </colgroup>
                                <thead>
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">원수사</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">설계사코드</th>
                                  </tr>
                                </thead>
                              </table>
                            </div>
                            {/* 본문 - 스크롤 가능 */}
                            <div className={`overflow-x-auto ${agentCodes.length >= 3 ? 'overflow-y-auto' : ''}`} style={agentCodes.length >= 3 ? { maxHeight: '200px', display: 'block' } : {}}>
                              <table className="w-full border-collapse table-fixed" style={agentCodes.length >= 3 ? { display: 'table' } : {}}>
                                <colgroup>
                                  <col style={{ width: '50%' }} />
                                  <col style={{ width: '50%' }} />
                                </colgroup>
                                <tbody>
                                  {sortedAgentCodes.map((code) => (
                                    <tr key={code.id} className="border-b border-gray-200 hover:bg-gray-50">
                                      <td className="px-4 py-3 text-sm text-gray-900">{code.companyName}</td>
                                      <td className="px-4 py-3 text-sm text-gray-900">
                                        {code.agentCode}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 알림 카드 영역 - GA 회원만 표시 */}
              {user?.companyType === 'GA' && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="bg-white rounded-lg border border-gray-200">
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center gap-2">
                        <HiOutlineBell className="text-gray-600" />
                        <h4 className="text-sm font-semibold text-gray-900">알림 설정</h4>
                      </div>
                    </div>
                    <div className="p-4">
                    <div>
                      <div className="mb-4 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm text-gray-900 mb-1">구글 캘린더 동기화</div>
                          <div className="text-xs text-gray-600 space-y-2">
                            {googleAccountsLoading && '구글 연동 상태를 불러오는 중입니다…'}
                            {!googleAccountsLoading && anyGoogleNeedsReauth && (
                              <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-amber-900">
                                <p className="font-medium leading-snug">{GOOGLE_CALENDAR_AUTH_EXPIRED_PROFILE_MESSAGE}</p>
                              </div>
                            )}
                            {!googleAccountsLoading &&
                              !anyGoogleNeedsReauth &&
                              (user?.googleCalendarSyncEnabled
                                ? anyGoogleCalendarConnected
                                  ? '구글 캘린더와 연동되어 있습니다.'
                                  : googleAccounts.length > 0
                                    ? '동기화는 켜져 있으나 아직 OAuth 연동이 완료되지 않았습니다. 아래 계정에서 연동을 진행하세요.'
                                    : '동기화가 활성화되었지만 등록된 구글 계정이 없습니다. 아래에서 계정을 추가하세요.'
                                : '구글 캘린더와 일정을 동기화하지 않습니다.')}
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={user?.googleCalendarSyncEnabled || false}
                            onChange={async (e) => {
                              try {
                                const response = await api.put('/api/users/profile', {
                                  googleCalendarSyncEnabled: e.target.checked,
                                  googleEmail: user?.googleEmail || '',
                                });
                                if (response && response.data) {
                                  onUpdate(response.data);
                                  showAlert('구글 캘린더 동기화 설정이 변경되었습니다.');
                                }
                              } catch (error) {
                                console.error('구글 캘린더 동기화 설정 변경 실패:', error);
                                showAlert('설정 변경에 실패했습니다.');
                              }
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      <div className="mt-3">
                        <div className="flex justify-between items-center mb-3">
                          <div className="font-semibold text-sm text-gray-900">구글 계정 목록</div>
                          <button
                            onClick={() => setAddingAccount(true)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-dashed border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                          >
                            <HiOutlinePlus />
                            계정 추가
                          </button>
                        </div>

                        {addingAccount && (
                  <div className="p-3 mb-3 bg-gray-50 rounded-md border border-gray-200">
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();

                        // 유효성 검사
                        const errors = {};
                        if (!newAccountData.googleEmail.trim()) {
                          errors.googleEmail = '구글 계정 이메일을 입력하세요!';
                        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newAccountData.googleEmail)) {
                          errors.googleEmail = '올바른 이메일 형식이 아닙니다.';
                        }

                        if (Object.keys(errors).length > 0) {
                          setNewAccountErrors(errors);
                          return;
                        }

                        try {
                          await api.post('/api/google-accounts', {
                            googleEmail: newAccountData.googleEmail,
                            accountName: newAccountData.accountName || newAccountData.googleEmail,
                          });
                          showAlert('구글 계정이 추가되었습니다. 이제 연동 버튼을 클릭하여 인증하세요.');
                          setNewAccountData({ googleEmail: '', accountName: '' });
                          setNewAccountErrors({});
                          setAddingAccount(false);
                          fetchGoogleAccounts();
                        } catch (error) {
                          console.error('구글 계정 추가 실패:', error);
                          showAlert(error.response?.data?.error || '구글 계정 추가에 실패했습니다.');
                        }
                      }}
                      className="flex items-end gap-2 flex-wrap"
                    >
                      <div className="flex-1 min-w-[200px]">
                        <input
                          type="email"
                          value={newAccountData.googleEmail}
                          onChange={(e) => {
                            setNewAccountData({ ...newAccountData, googleEmail: e.target.value });
                            if (newAccountErrors.googleEmail) setNewAccountErrors({ ...newAccountErrors, googleEmail: '' });
                          }}
                          placeholder="구글 계정 이메일"
                          className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            newAccountErrors.googleEmail ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {newAccountErrors.googleEmail && (
                          <p className="mt-1 text-xs text-red-600">{newAccountErrors.googleEmail}</p>
                        )}
                      </div>
                      <div className="flex-1 min-w-[150px]">
                        <input
                          type="text"
                          value={newAccountData.accountName}
                          onChange={(e) => setNewAccountData({ ...newAccountData, accountName: e.target.value })}
                          placeholder="계정 별칭 (선택)"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          className="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                        >
                          추가
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setAddingAccount(false);
                            setNewAccountData({ googleEmail: '', accountName: '' });
                            setNewAccountErrors({});
                          }}
                          className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          취소
                        </button>
                      </div>
                    </form>
                  </div>
                        )}

                        {googleAccounts.length === 0 ? (
                  <div className="text-gray-500 text-xs py-3 px-3 text-center bg-gray-50 rounded-md">
                    등록된 구글 계정이 없습니다. 위의 "계정 추가" 버튼을 클릭하여 구글 계정을 추가하세요.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {googleAccounts.map((account) => (
                      <div key={account.id} className="flex min-w-0 flex-col gap-2 rounded-md border border-gray-200 p-3 transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="mb-1 flex min-w-0 flex-wrap items-center gap-2">
                            <span className="min-w-0 break-words text-sm font-medium text-gray-900">
                              {account.accountName || account.googleEmail}
                            </span>
                            {account.isDefault && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                기본
                              </span>
                            )}
                            {account.needsReauth && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900">
                                인증 만료
                              </span>
                            )}
                            {account.isConnected && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                연동됨
                              </span>
                            )}
                          </div>
                          <div className="min-w-0 break-words text-xs text-gray-500">
                            {account.googleEmail}
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center gap-2">
                          <button
                            onClick={async () => {
                              // 이미 인증이 진행 중이면 무시
                              if (isAuthInProgress) {
                                return;
                              }

                              // 이미 열려있는 팝업이 있으면 먼저 닫기
                              try {
                                if (activeAuthWindowRef.current && !activeAuthWindowRef.current.closed) {
                                  activeAuthWindowRef.current.close();
                                }
                              } catch (error) {
                                // COOP 정책으로 인한 접근 차단 시 무시
                                console.debug('창 닫기 중 오류 (COOP 정책):', error);
                              }

                              // 이전 핸들러와 interval 정리
                              if (activeMessageHandlerRef.current) {
                                window.removeEventListener('message', activeMessageHandlerRef.current);
                                activeMessageHandlerRef.current = null;
                              }
                              if (activeCheckIntervalRef.current) {
                                clearInterval(activeCheckIntervalRef.current);
                                activeCheckIntervalRef.current = null;
                              }

                              // 인증 진행 중 플래그 설정
                              setIsAuthInProgress(true);

                              try {
                                const response = await api.get(`/api/calendar/oauth/authorize?accountId=${account.id}`);
                                if (response && response.data && response.data.authUrl) {
                                  // 고유한 팝업 이름 생성 (타임스탬프 + 계정 ID)
                                  const uniqueWindowName = `google_auth_${account.id}_${Date.now()}`;

                                  const authWindow = window.open(
                                    response.data.authUrl,
                                    uniqueWindowName,
                                    'width=500,height=600,scrollbars=yes'
                                  );

                                  if (!authWindow) {
                                    setIsAuthInProgress(false);
                                    showAlert('팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용해주세요.');
                                    return;
                                  }

                                  activeAuthWindowRef.current = authWindow;

                                  const messageHandler = (event) => {
                                    // 이 핸들러가 활성 핸들러인지 확인
                                    if (activeMessageHandlerRef.current !== messageHandler) {
                                      return;
                                    }

                                    if (event.data && event.data.type === 'GOOGLE_AUTH_SUCCESS') {
                                      window.removeEventListener('message', messageHandler);
                                      if (activeCheckIntervalRef.current) {
                                        clearInterval(activeCheckIntervalRef.current);
                                        activeCheckIntervalRef.current = null;
                                      }
                                      activeMessageHandlerRef.current = null;
                                      activeAuthWindowRef.current = null;
                                      setIsAuthInProgress(false);
                                      fetchGoogleAccounts();
                                      api.get('/api/users/profile')
                                        .then(profileResponse => {
                                          if (profileResponse && profileResponse.data) {
                                            onUpdate(profileResponse.data);
                                          }
                                        });
                                      showAlert('구글 계정 연동이 완료되었습니다.');
                                    } else if (event.data && event.data.type === 'GOOGLE_AUTH_ERROR') {
                                      window.removeEventListener('message', messageHandler);
                                      if (activeCheckIntervalRef.current) {
                                        clearInterval(activeCheckIntervalRef.current);
                                        activeCheckIntervalRef.current = null;
                                      }
                                      activeMessageHandlerRef.current = null;
                                      activeAuthWindowRef.current = null;
                                      setIsAuthInProgress(false);
                                      showAlert(event.data.message || '구글 계정 연동에 실패했습니다.');
                                    }
                                  };

                                  window.addEventListener('message', messageHandler);
                                  activeMessageHandlerRef.current = messageHandler;

                                  const checkClosed = setInterval(() => {
                                    try {
                                      // Cross-Origin-Opener-Policy 정책으로 인해 접근이 차단될 수 있음
                                      if (authWindow.closed) {
                                        clearInterval(checkClosed);
                                        window.removeEventListener('message', messageHandler);
                                        activeMessageHandlerRef.current = null;
                                        activeCheckIntervalRef.current = null;
                                        activeAuthWindowRef.current = null;
                                        setIsAuthInProgress(false);
                                        fetchGoogleAccounts();
                                      }
                                    } catch (error) {
                                      // COOP 정책으로 인한 접근 차단 시 간격을 늘리거나 무시
                                      // 메시지 핸들러가 성공/실패를 처리하므로 여기서는 오류만 로깅
                                      console.debug('창 상태 확인 중 오류 (COOP 정책):', error);
                                    }
                                  }, 1000);

                                  activeCheckIntervalRef.current = checkClosed;
                                } else {
                                  // 응답은 성공했지만 authUrl이 없는 경우
                                  setIsAuthInProgress(false);
                                  showAlert('인증 URL을 받을 수 없습니다. 다시 시도해주세요.');
                                }
                              } catch (error) {
                                console.error('구글 계정 연동 실패:', error);
                                showAlert(error.response?.data?.error || '구글 계정 연동에 실패했습니다.');
                                activeAuthWindowRef.current = null;
                                activeMessageHandlerRef.current = null;
                                activeCheckIntervalRef.current = null;
                                setIsAuthInProgress(false);
                              }
                            }}
                            disabled={isAuthInProgress}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded transition-colors ${
                              isAuthInProgress
                                ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                                : account.isConnected
                                  ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                                  : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                            }`}
                          >
                            <HiOutlineLink />
                            {account.isConnected ? '재연동' : '연동'}
                          </button>
                          {account.isConnected && (
                            <button
                              type="button"
                              onClick={() => {
                                setDisconnectingAccountId(account.id);
                                setDisconnectConfirmVisible(true);
                              }}
                              className="px-3 py-1.5 text-sm text-amber-700 hover:text-amber-800 hover:bg-amber-50 rounded transition-colors"
                            >
                              연동 해제
                            </button>
                          )}
                          {!account.isDefault && (
                            <button
                              onClick={() => {
                                setDefaultAccountId(account.id);
                                setDefaultConfirmVisible(true);
                              }}
                              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
                            >
                              기본 설정
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setDeletingAccountId(account.id);
                              setDeleteConfirmVisible(true);
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          >
                            <HiOutlineTrash />
                            삭제
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-xs font-medium text-gray-500 mb-2">서비스 이용약관</p>
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 text-sm text-gray-700">
                {(user?.companyType === 'GA' || user?.companyType === 'IS') && (
                  <span>
                    동의일시:{' '}
                    {user?.serviceTermsAgreedAt
                      ? dayjs(user.serviceTermsAgreedAt).format('YYYY-MM-DD HH:mm')
                      : '-'}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setServiceTermsModalVisible(true)}
                  className="text-blue-600 hover:text-blue-700 hover:underline inline-flex items-center gap-1 w-fit text-left"
                >
                  <HiOutlineLink className="w-4 h-4 shrink-0" />
                  서비스 이용약관 보기
                </button>
              </div>
            </div>
        </div>
      </div>

      {/* 삭제 확인 모달 */}
      {deleteConfirmVisible && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                구글 계정 삭제
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                이 구글 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setDeleteConfirmVisible(false);
                    setDeletingAccountId(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={async () => {
                    try {
                      await api.delete(`/api/google-accounts/${deletingAccountId}`);
                      showAlert('구글 계정이 삭제되었습니다.');
                      setDeleteConfirmVisible(false);
                      setDeletingAccountId(null);
                      fetchGoogleAccounts();
                    } catch (error) {
                      showAlert('구글 계정 삭제에 실패했습니다.');
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 transition-colors"
                >
                  삭제
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 구글 캘린더 연동 해제 확인 (OAuth만 제거, 계정 행 유지) */}
      {disconnectConfirmVisible && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                캘린더 연동 해제
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                이 구글 계정의 캘린더 연동(OAuth)만 해제합니다. 목록에서 계정은 그대로 남으며, 이후 「연동」으로 다시 인증할 수 있습니다.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setDisconnectConfirmVisible(false);
                    setDisconnectingAccountId(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await api.post(`/api/google-accounts/${disconnectingAccountId}/disconnect`);
                      showAlert('캘린더 연동이 해제되었습니다.');
                      setDisconnectConfirmVisible(false);
                      setDisconnectingAccountId(null);
                      fetchGoogleAccounts();
                      api.get('/api/users/profile')
                        .then((profileResponse) => {
                          if (profileResponse && profileResponse.data) {
                            onUpdate(profileResponse.data);
                          }
                        });
                    } catch (error) {
                      showAlert(error.response?.data?.error || '연동 해제에 실패했습니다.');
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-amber-600 rounded-md hover:bg-amber-700 transition-colors"
                >
                  연동 해제
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 기본 계정 설정 확인 모달 */}
      {defaultConfirmVisible && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                기본 계정 설정
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                이 계정을 기본 계정으로 설정하시겠습니까?
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setDefaultConfirmVisible(false);
                    setDefaultAccountId(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={async () => {
                    try {
                      await api.put(`/api/google-accounts/${defaultAccountId}`, { isDefault: true });
                      showAlert('기본 계정이 변경되었습니다.');
                      setDefaultConfirmVisible(false);
                      setDefaultAccountId(null);
                      fetchGoogleAccounts();
                    } catch (error) {
                      showAlert('기본 계정 변경에 실패했습니다.');
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 상위관리자 선택 모달 */}
      <SupervisorSelection
        visible={supervisorModalVisible}
        onClose={() => setSupervisorModalVisible(false)}
        onSelect={(supervisorId, supervisorEmail, supervisorName) => {
          setTempSupervisorId(supervisorId);
          setTempSupervisorName(supervisorName || '');
          setTempSupervisorEmail(supervisorEmail || '');
          setSupervisorModalVisible(false);
        }}
        onCancel={() => {
          setTempSupervisorId(originalSupervisorId);
          setTempSupervisorName(originalSupervisorName);
          setTempSupervisorEmail(originalSupervisorEmail);
        }}
        currentUserId={user?.id}
        initialSupervisorId={tempSupervisorId}
        initialSupervisorEmail={tempSupervisorEmail}
        showAlert={showAlert}
        allUsers={[]}
        divisionId={selectionDivisionId}
      />

      {/* 대직 담당자 선택 모달 */}
      <SubstituteUserSelection
        visible={substituteModalVisible}
        onClose={() => setSubstituteModalVisible(false)}
        availableSubstituteUsers={availableSubstituteUsers}
        selectedSubstituteUserId={tempSubstituteUserId}
        onSelectSubstituteUser={handleSelectTempSubstituteUser}
        currentUser={user}
        showAlert={showAlert}
      />

      <AvatarCropModal
        open={Boolean(cropFile)}
        file={cropFile}
        onCancel={() => setCropFile(null)}
        onConfirm={handleAvatarCropConfirm}
      />

      <AvatarPreviewModal
        open={avatarPreviewOpen}
        imageUrl={avatarUrl}
        message={user?.profileMessage}
        onClose={() => setAvatarPreviewOpen(false)}
      />

      {assignedWorkplaceModalVisible && user?.companyType === 'IS' && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">배정사업장</h3>
            </div>
            <div className="px-6 py-4 overflow-y-auto">
              {isOrgLoading ? (
                <p className="text-sm text-gray-500">배정 사업장을 불러오는 중...</p>
              ) : isOrg == null ? (
                <p className="text-sm text-gray-500">배정 사업장을 불러오지 못했습니다.</p>
              ) : (isOrg.workplaces || []).length === 0 ? (
                <p className="text-sm text-gray-500">배정된 사업장이 없습니다.</p>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">GA</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">사업장</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(isOrg.workplaces || []).map((workplace, index) => (
                        <tr key={`${workplace.companyName || ''}-${workplace.divisionName || ''}-${index}`} className="border-t border-gray-200">
                          <td className="px-4 py-3 text-sm text-gray-900">{workplace.companyName || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{workplace.divisionName || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
              <button
                type="button"
                onClick={() => setAssignedWorkplaceModalVisible(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 비밀번호 변경 모달 */}
      <ServiceTermsViewModal
        visible={serviceTermsModalVisible}
        onClose={() => setServiceTermsModalVisible(false)}
      />

      {passwordModalVisible && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">비밀번호 변경</h3>
                <button
                  onClick={() => setPasswordModalVisible(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="px-6 py-4">
              <PasswordChangeForm
                onSuccess={() => {
                  setPasswordModalVisible(false);
                  showAlert('비밀번호가 변경되었습니다.');
                }}
                onCancel={() => setPasswordModalVisible(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 비밀번호 변경 폼 (기존 비밀번호 확인 방식만 지원)
const PasswordChangeForm = ({ onSuccess, onCancel }) => {
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // 유효성 검사
    const newErrors = {};
    if (!formData.oldPassword.trim()) {
      newErrors.oldPassword = '기존 비밀번호를 입력해주세요.';
    }
    if (!formData.newPassword.trim()) {
      newErrors.newPassword = '새 비밀번호를 입력해주세요.';
    } else if (formData.newPassword.length < 11) {
      newErrors.newPassword = '비밀번호는 최소 11자 이상이어야 합니다.';
    }
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = '비밀번호 확인을 입력해주세요.';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호가 일치하지 않습니다.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      await api.post('/api/users/password/change-with-old', {
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword
      });
      onSuccess();
    } catch (error) {
      console.error('비밀번호 변경 실패:', error);
      showAlert(error.response?.data?.error || '비밀번호 변경에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">기존 비밀번호</label>
        <PasswordInput
          value={formData.oldPassword}
          onChange={(e) => setFormData({ ...formData, oldPassword: e.target.value })}
          className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.oldPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
          }`}
          placeholder="기존 비밀번호를 입력하세요"
        />
        {errors.oldPassword && (
          <p className="mt-1 text-xs text-red-600">{errors.oldPassword}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">새 비밀번호</label>
        <PasswordInput
          value={formData.newPassword}
          onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
          className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.newPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
          }`}
          placeholder="새 비밀번호를 입력하세요 (최소 11자)"
        />
        {errors.newPassword && (
          <p className="mt-1 text-xs text-red-600">{errors.newPassword}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">새 비밀번호 확인</label>
        <PasswordInput
          value={formData.confirmPassword}
          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
          className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
          }`}
          placeholder="새 비밀번호를 다시 입력하세요"
        />
        {errors.confirmPassword && (
          <p className="mt-1 text-xs text-red-600">{errors.confirmPassword}</p>
        )}
      </div>

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors"
        >
          취소
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {loading ? '변경중...' : '비밀번호 변경'}
        </button>
      </div>
    </form>
  );
};

export default ProfileModal;
